import { Button, Divider, Empty, Switch, Tooltip } from "antd";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import CheckboxDropdown from "../components/CheckboxDropdown";
import { getPhasesAndTasks } from "../services/api-server/phases";
import { GetAntIcon } from "../utils/ant_icons";
import { setLookupData } from "../utils/lookupFinder";
import { HomeContext } from "./Home";
import NoAccess from "./NoAccess";

import dayjs from "dayjs";
import elementalSvg from "../assets/image/elemental 'e'.svg";
import AuditLogButton from "../components/AuditLogButton";
import GanttChart from "../components/Charts/GanttChart";
import ExpandableText from "../components/ExpandableText";
import InputSearch from "../components/InputSearch";
import AuditLogModal from "../components/Modal/AuditLogModal";
import ProtectedModuleRoute from "../components/ProtectedModuleRoute";
import SharepointComponent from "../components/SharepointComponent";
import TabButton from "../components/TabButton";
import useCheckModule from "../hooks/useCheckModule";
import useDocumentsTab from "../hooks/useDocumentsTab";
import useHandleContextMenu from "../hooks/useHandleContextMenu";
import usePermission from "../hooks/usePermission";
import { usePermissionStore } from "../stores/permissionStore";
import { getDeliverablesGeneral } from "../services/api-server/deliverables";
import { getOperations } from "../services/api-server/operations";
import { getProject } from "../services/api-server/projects";
import { getWellProgrammeTemplateList } from "../services/api-server/well_programme_template";
import { getWells } from "../services/api-server/wells";
import { excludedMark, statusIcon } from "../utils/statusIcon";
// import {
// 	getDeliverables,
// 	getDeliverablesList,
// } from "../services/api-server/deliverables";
const WellTemplateExample: any = [
	{ label: "Operation name", key: "operation_name" },
	{ label: "Operation type", key: "operation_type" },
	{ label: "Est. duration (days)", key: "estimated_duration_days" },
];

const WellDetailsExample: any = [
	{ label: "Location", key: "location" },
	{ label: "Latitude", key: "latitude" },
	{ label: "Longitude", key: "longitude" },
	{ label: "Project template", key: "project_template" },
	{ label: "Block", key: "block" },
	{ label: "Field", key: "field" },
	{ label: "Platform", key: "platform" },
	{ label: "Water depth", key: "water_depth", suffix: "meters" },
	{ label: "License", key: "license" },
	{ label: "Purpose", key: "well_purpose" },
	{ label: "Status", key: "well_status" },
];

function WellOverview(props: any) {
	const context: any = useContext(HomeContext);
	const location = useLocation();
	const navigate = useNavigate();
	const [wellInfo, setWellInfo] = useState<any>(null);
	const [operationInfo, setOperationInfo] = useState<any>(null);
	const [wellTitle, setWellTitle] = useState<any>(null);
	const [projectInfo, setProjectInfo] = useState<any>(null);
	const [projectTitle, setProjectTitle] = useState<any>(null);
	const [wellDetailsExpand, setWellDetailsExpand] = useState<any>(false);
	const [activePage, setActivePage] = useDocumentsTab("kanban");
	const [filterMode, setFilterMode] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<any>("");

	const { handleContextMenu } = useHandleContextMenu();

	const isExaminerOnly = usePermissionStore(
		(state) => !!state.accessProfile?.restricted
	);
	const hasViewOperationPermission = usePermission(
		"View.Operation",
		projectInfo?.project_code
	);
	const hasEditOperationPermission = usePermission(
		"Edit.Operation",
		projectInfo?.project_code
	);
	const hasDeleteOperationPermission = usePermission(
		"Delete.Operation",
		projectInfo?.project_code
	);

	const { checkModule } = useCheckModule();

	const [projectRoleAssignmentList, setProjectRoleAssignmentList] =
		useState<any>([]);

	const [phaseList, setPhaseList] = useState<any>(null);
	const [filteredPhaseList, setFilteredPhaseList] = useState<any>(null);
	const [taskList, setTaskList] = useState<any>(null);
	const [filteredTaskList, setFilteredTaskList] = useState<any>(null);

	const [isLoading, setIsLoading] = useState<any>(true);

	const [selectedFilters, setSelectedFilters] = useState<any>({
		responsible_roles: [],
		accountable_roles: [],
		consulted: [],
		informed_parties: [],
		status: [],
	});

	const [hideExcluded, setHideExcluded] = useState<any>(true);

	const [sharepointDisabled, setSharepointDisabled] = useState<boolean>(true);

	//Audit Logs states
	const [auditOpen, setAuditOpen] = useState<any>(false);

	//Gantt data
	const [ganttData, setGanttData] = useState<any>([]);
	const [tableHeight, setTableHeight] = useState<any>(null);
	const [filteredGanttTableData, filterGanttTableData] = useState<any>(null);
	const [searchRecord, setSearchRecord] = useState<any>([]);

	const calculateTableHeight = () => {
		const container: any = document.getElementById("gantt-container");
		if (container) {
			setTableHeight(container?.clientHeight * 0.35 - 120);
		}
	};

	//Setting search record
	const handleSearch = (value: any) => {
		let record: any = [...searchRecord];
		let recordIndex = record.findIndex((item: any) => {
			return item.columnKey === value.columnKey;
		});
		if (recordIndex !== -1) {
			if (value.value != "") {
				record[recordIndex] = value;
				setSearchRecord(record);
			} else {
				record.splice(recordIndex, 1);
				setSearchRecord(record);
			}
		} else {
			if (value.value != "") {
				record.push(value);
				setSearchRecord(record);
			}
		}

		//Filter data and table columns based on search record
		let data: any = [...filteredPhaseList];
		let filteredData = data
			?.map((element: any) => {
				let bool = record.every((item: any) => {
					if (Array.isArray(element[item.columnKey])) {
						return element[item.columnKey].some((arrayItem: any) =>
							arrayItem.toLowerCase().includes(item.value.toLowerCase())
						);
					} else {
						return element[item.columnKey]
							?.toLowerCase()
							.includes(item.value.toLowerCase());
					}
				});
				if (bool) return element;
			})
			.filter((element: any) => element !== null && element !== undefined);
		filterGanttTableData(filteredData);
		data = filteredData;
	};

	const setWellContent = (currentWellData: any, infoKey: any) => {
		let content = "-";
		if (
			currentWellData[infoKey] ||
			infoKey == "latitude" ||
			infoKey == "longitude"
		) {
			content = setLookupData(context.allData, currentWellData, infoKey);
			if (infoKey === "location") {
				content =
					currentWellData[infoKey].charAt(0).toUpperCase() +
						currentWellData[infoKey].slice(1) || "-";
			}
		}
		return content;
	};

	useEffect(() => {
		let filteredTaskList = null;
		if (searchValue != "") {
			let filteredTask: any = {};

			filteredTask = taskList.filter(
				(task: any) =>
					task?.task_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
					task.task_number === searchValue
			);
			filteredTaskList = filteredTask;
		} else {
			filteredTaskList = taskList;
		}

		const check = Object.keys(selectedFilters).some(
			(filterKey: any) => selectedFilters[filterKey].length > 0
		);
		if (check) {
			filteredTaskList = filteredTaskList.filter((task: any) =>
				Object.keys(selectedFilters).every((filterKey: any) => {
					if (selectedFilters[filterKey].length == 0) {
						return true;
					}
					if (typeof task[filterKey] == "object") {
						return task[filterKey].some((key: any) =>
							selectedFilters[filterKey].includes(key)
						);
					} else {
						return selectedFilters[filterKey].includes(task[filterKey]);
					}
				})
			);
		}

		setFilteredTaskList(filteredTaskList);
	}, [searchValue, selectedFilters]);

	useEffect(() => {
		filterToggleData(phaseList, taskList);
	}, [hideExcluded]);

	// set filterMode to false if activePage is set to documents
	useEffect(() => {
		if (activePage === "documents") {
			setFilterMode(false);
		}
	}, [activePage]);

	// const calculateGanttData = (phaseList: any, taskList: any) => {
	// 	let ganttData = phaseList
	// 		?.filter(
	// 			(phase: any) =>
	// 				phase.planned_start_date && phase.estimated_duration_days
	// 		)
	// 		.map((phase: any, index: any) => {
	// 			let title = phase?.phase_name;
	// 			let startDate = new Date(phase?.planned_start_date);
	// 			let endDate = new Date(
	// 				startDate.getTime() +
	// 					phase?.estimated_duration_days * 24 * 60 * 60 * 1000
	// 			);
	// 			let tooltip = `Title: ${title}<br>Planned start date: ${dayjs(
	// 				startDate
	// 			).format("D MMM YYYY")}<br>Planned end date: ${dayjs(endDate).format(
	// 				"D MMM YYYY"
	// 			)}<br>Estimated duration: ${phase.estimated_duration_days} days`;
	// 			let color = phase.disabled ? "#686868" : "#299BA3";

	// 			const relatedTasks = taskList.filter((task: any) => {
	// 				return task.phase_id === phase._id;
	// 			});

	// 			console.log("relatedTasks: ", relatedTasks);

	// 			return {
	// 				...phase,
	// 				title,
	// 				startDate,
	// 				endDate,
	// 				value: phaseList?.length - index,
	// 				color,
	// 				tooltip,
	// 				relatedTasks,
	// 			};
	// 		});

	// 	setGanttData(ganttData);
	// };

	const calculateGanttData = (phaseList: any[], taskList: any[]) => {
		const ganttData: any[] = [];

		// console.log("phaseList: ", phaseList);
		// console.log("taskList: ", taskList);

		// Crash because if one of the phase is missing planned_start_date or estimated_duration_days, therefore, cannot init the date range calculation

		const validPhaseList = phaseList?.filter((phase) => {
			return phase.planned_start_date && phase.estimated_duration_days;
		});

		// console.log("validPhaseList: ", validPhaseList);

		validPhaseList?.forEach((phase, index) => {
			const title = phase.phase_name;
			const startDate =
				phase.planned_start_date && new Date(phase.planned_start_date);
			const endDate =
				phase.planned_start_date &&
				new Date(
					startDate.getTime() +
						phase.estimated_duration_days * 24 * 60 * 60 * 1000
				);
			const tooltip = `Title: ${title}<br>Planned start date: ${
				startDate && dayjs(startDate).format("D MMM YYYY")
			}<br>Planned end date: ${
				endDate && dayjs(endDate).format("D MMM YYYY")
			}<br>Estimated duration: ${phase?.estimated_duration_days} days`;
			const color = phase.disabled ? "#686868" : "#299BA3";

			// Add the phase itself
			ganttData.push({
				...phase,
				title,
				startDate,
				endDate,
				value: validPhaseList.length - index,
				color,
				tooltip,
				type: "phase",
			});

			const relatedTasks = taskList.filter(
				(task) =>
					task.phase_id === phase._id &&
					task.planned_start_date &&
					task.estimated_duration_days
			);

			relatedTasks.forEach((task, taskIndex) => {
				const taskTitle = task.task_name;
				const taskStartDate =
					task.planned_start_date && new Date(task.planned_start_date);
				const taskEndDate =
					task.planned_start_date &&
					new Date(
						taskStartDate.getTime() +
							task.estimated_duration_days * 24 * 60 * 60 * 1000
					);
				const taskTooltip = `Task: ${task.task_name}<br>Start: ${
					taskStartDate && dayjs(taskStartDate).format("D MMM YYYY")
				}<br>End: ${
					taskEndDate && dayjs(taskEndDate).format("D MMM YYYY")
				}<br>Duration: ${task.estimated_duration_days} days`;

				ganttData.push({
					...task,
					title: taskTitle,
					startDate: taskStartDate,
					endDate: taskEndDate,
					value: validPhaseList.length - index - (taskIndex + 1) * 0.01, // small offset for visual stacking
					color: "#56B3B4", // differentiate task color
					tooltip: taskTooltip,
					type: "task", // optional marker
				});
			});
		});

		setGanttData(ganttData);
	};

	const filterToggleData = (phaseList: any, taskList: any) => {
		if (hideExcluded) {
			const filteredPhaseList = phaseList?.filter((phase: any) => {
				return !phase.disabled;
			});
			setFilteredPhaseList(filteredPhaseList);
			calculateGanttData(filteredPhaseList, taskList);
			calculateTableHeight();
		} else {
			setFilteredPhaseList(phaseList);
			calculateGanttData(phaseList, taskList);
			calculateTableHeight();
		}
	};

	useEffect(() => {
		window.addEventListener("resize", () => {
			calculateTableHeight();
		});

		if (context.currentTenantKey && context.allData) {
			let project_code: any =
				location?.state?.project_code || location?.pathname?.split("/")[2];
			let operation_code: any =
				location?.state?.operation_code || location?.pathname?.split("/")[3];

			getProject(context.currentTenantKey, project_code)
				.then((project: any) => {
					if (project) {
						setProjectTitle(project.project_name);
						setProjectInfo(project);
						let role1 = [
							{ label: "Project manager", value: "project_manager" },
						];
						let role2 = project.role_assignments.map((role: any) => {
							return { label: role.role, value: role.role_id };
						});
						let roles = role1.concat(role2);
						setProjectRoleAssignmentList(roles);
						context.setCurrentProject(project);
						getOperations(context.currentTenantKey, { operation_code }).then(
							(operationData: any) => {
								setOperationInfo(operationData[0]);
								getWells(context.currentTenantKey, {
									_id: operationData[0].well_id,
								})
									.then((wellData: any) => {
										const data: any = {
											...operationData[0],
											...wellData[0],
										};
										setWellTitle(data.operation_name);
										setWellInfo(data);
										context.setCurrentOperation(operationData[0]);

										getPhasesAndTasks(context.currentTenantKey, {
											operation_code: data.operation_code,
										}).then((data: any) => {
											setPhaseList(data?.phase_data || []);
											filterToggleData(
												data?.phase_data || [],
												data?.taskList || []
											); // set filteredPhaseList to phaseList
											const taskIDArray = data?.taskList?.map(
												(task: any) => task._id
											);
											getDeliverablesGeneral(
												context.currentTenantKey,
												taskIDArray
											)
												.then((deliverableList: any) => {
													deliverableList?.forEach((deliverable: any) => {
														let responsibleRoles =
															deliverable.responsible_roles || [];
														let accountable_roles =
															deliverable.accountable_roles || [];
														let consulted = deliverable.consulted || [];
														let informed_parties =
															deliverable.informed_parties || [];
														let foundTaskIndex = data?.taskList.findIndex(
															(task: any) => task._id === deliverable.task_id
														);
														if (foundTaskIndex != -1) {
															if (!data.taskList[foundTaskIndex].assigned_to) {
																data.taskList[foundTaskIndex].assigned_to = [];
															}
															data.taskList[foundTaskIndex].assigned_to =
																data.taskList[foundTaskIndex].assigned_to
																	.concat(responsibleRoles)
																	.concat(accountable_roles)
																	.concat(consulted)
																	.concat(informed_parties)
																	.filter(
																		(item: any, index: any, arr: any) =>
																			arr.indexOf(item) === index
																	);
														}
													});
													setIsLoading(false);
													console.log("data?.taskList: ", data?.taskList);

													// 1.  Pull templates
													getWellProgrammeTemplateList(
														context.currentTenantKey
													).then((templateList: any) => {
														// 2.  Pick the template that matches your task list
														const template = templateList.find(
															(t: any) =>
																t._id === data.taskList[0]?.template_id
														);
														console.log("template: ", template);
														if (!template) return;

														console.log("data: ", data);
														// 3.  Inject sequence_id into every live task
														data.taskList.forEach((task: any) => {
															const phaseName = data.phase_data.find(
																(phase: any) => phase._id === task.phase_id
															)?.phase_name;

															console.log("phaseName: ", phaseName);

															// if (!phaseName) {
															// 	task.sequence_id = null;
															// 	return;
															// }

															console.log("d phaseName: ", phaseName);

															// 3a. find the matching phase inside the template
															const phase = template.phases.find(
																(p: any) => p.phase_name === phaseName
															);
															// if (!phase) {
															// 	task.sequence_id = null;
															// 	return;
															// }

															console.log("d phase: ", phase);

															// 3b. find the matching task inside that phase
															const tplTask = phase.tasks.find(
																(t: any) =>
																	t.task_name === task.task_name ||
																	t.task_number === task.task_number
															);

															console.log("d tplTask: ", tplTask);

															// 3c. inject the sequence_id (or null if not found)
															// task.sequence_id = tplTask
															// 	? tplTask.sequence_id
															// 	: null;
															console.log(
																"d task.sequence_id: ",
																task.sequence_id
															);
														});
														console.log("d data?.taskList: ", data?.taskList);
														// 4.  Done – if you’re using React state, call setTaskList(data.taskList)
													});

													console.log("data?.taskList again: ", data?.taskList);
													setTaskList(data?.taskList);
													// if (localStorage.getItem("selectedFilters")) {
													// 	let selectedFiltersStore: any =
													// 		localStorage.getItem("selectedFilters");
													// 	let selectedFiltersStoreJson =
													// 		JSON.parse(selectedFiltersStore);
													// 	setSelectedFilters(selectedFiltersStoreJson);
													// 	setFilterMode(true);
													// }
												})
												.catch((error: any) => {
													console.log(error);
												});
										});
									})
									.catch((error: any) => {
										setWellTitle(null);
										setWellInfo(null);
										setIsLoading(false);
									});
							}
						);
					} else {
						setProjectTitle(null);
						setProjectInfo(null);
						setIsLoading(false);
					}
				})
				.catch((e: any) => {
					setProjectTitle(null);
					setProjectInfo(null);
					setIsLoading(false);
				});
		}
	}, [context.currentTenantKey, context.allData]);

	useMemo(() => {}, [activePage]);

	const hasSelectedFilters = () => {
		// Check if any of the filter categories have selected values
		return Object.values(selectedFilters).some(
			(values: any) => values.length > 0
		);
	};
	const handleMenuClick = (field: any, selectedValues: any[]) => {
		// console.log(`Selected: ${field} - ${selectedValues}`);
		// Update the selected filters state
		setSelectedFilters({ ...selectedFilters, [field]: selectedValues });

		localStorage.setItem(
			"selectedFilters",
			JSON.stringify({ ...selectedFilters, [field]: selectedValues })
		);
	};

	const handleClearAllFilters = () => {
		// Reset all filters to empty arrays
		setSelectedFilters({
			responsible_roles: [],
			accountable_roles: [],
			consulted: [],
			informed_parties: [],
			status: [],
		});
		setFilterMode(false);

		// Clear session storage for filter values and checkboxes
		localStorage.removeItem("selectedFilters");

		// Object.keys(selectedFilters).forEach((field) => {
		// 	const storageKey = generateStorageKey(field);
		// 	localStorage.removeItem(storageKey);
		// });

		setTimeout(() => {
			setFilterMode(true);
		}, 5);
	};

	const renderMetaData = () => {
		const numRows = Math.ceil(Object.keys(WellTemplateExample).length / 2);
		const totalColumns = numRows * 2;
		const leftOverColumns = totalColumns - WellTemplateExample.length;

		return (
			<>
				<div
					className="meta-data-container"
					style={{ gridTemplateColumns: "repeat(3, auto)" }}
				>
					{WellTemplateExample.map((element: any, index: any) => (
						<div key={index} className="meta-data-item">
							<span className="panel-detail-label">{element.label}</span>
							<span className="panel-item-content">
								{setWellContent(wellInfo, element.key)}
							</span>
						</div>
					))}
				</div>
				<div className="panel-item-small-container">
					<span className="panel-detail-label">Project comment</span>
					<span className="panel-item-content">
						<ExpandableText
							paragraphText={setWellContent(wellInfo, "well_project_comments")}
							rows={5}
							containerId="main-container"
						/>
					</span>
				</div>
			</>
		);
	};

	const renderDetailData = () => {
		const numRows = Math.ceil(Object.keys(WellDetailsExample).length / 3);
		const totalColumns = numRows * 3;
		const leftOverColumns = totalColumns - WellDetailsExample.length;

		return (
			<div className="extra-panel-container">
				<div
					style={{
						display: "grid",
						columnGap: "20px",
						rowGap: "20px",
						gridTemplateRows: `repeat(4, auto)`,
						gridTemplateColumns: "repeat(3, auto)",
						gridAutoFlow: "column",
						flexGrow: 1,
					}}
				>
					{WellDetailsExample.map((element: any) => (
						<div
							style={{
								display: "grid",
								columnGap: "20px",
								gridTemplateRows: "20px auto",
							}}
						>
							<span className="panel-detail-label">{element.label}</span>
							<span className="panel-item-content">
								{setWellContent(wellInfo, element.key)}
							</span>
						</div>
					))}
					{Array.from({ length: leftOverColumns }, (_, index) => (
						<div
							style={{
								display: "grid",
								rowGap: "10px",
								gridTemplateRows: "auto auto",
							}}
						>
							<span key={index}>{""}</span>
							<span key={index}>{""}</span>
						</div>
					))}
				</div>
				<div className="panel-item-small-container">
					<span className="panel-detail-label">Well comment</span>
					<span className="panel-item-content" style={{ verticalAlign: "top" }}>
						<ExpandableText
							paragraphText={setWellContent(wellInfo, "well_comments")}
							rows={5}
							containerId="main-container"
						/>
					</span>
				</div>
			</div>
		);
	};

	const renderKanbanColumns = () => {
		const renderTasks = (phase: any) => {
			let currentTaskList = filteredTaskList || taskList;
			if (hideExcluded) {
				currentTaskList = currentTaskList.filter((task: any) => !task.disabled);
			}
			const phaseTaskList = currentTaskList.filter(
				(task: any) => task.phase_id == phase._id
			);
			// .sort((a: any, b: any) => {
			// 	if (a.sequence_id == null) return 1;
			// 	if (b.sequence_id == null) return -1;
			// 	return a.sequence_id - b.sequence_id;
			// });
			return phaseTaskList?.map((task: any) => {
				const disabled = task.disabled || false;
				const comment = task.disabled_reason || "";
				const tooltipText =
					!disabled || comment == "" ? (
						""
					) : (
						<span>
							This task is excluded from the well programme. You may still click
							to view the details. <br /> <br />
							<b>Comment:</b> <br />
							{comment}
						</span>
					);
				return (
					<div
						className={!disabled ? "task-item" : "task-item task-item-disabled"}
						onClick={() => {
							navigate(`${phase._id}/${task._id}`, {
								replace: false,
								state: { task_id: task._id },
							});
						}}
						onContextMenu={(e: any) => {
							const location = `${window.location.href}/${phase._id}/${task._id}`;
							handleContextMenu(e, { link: location });
						}}
					>
						<Tooltip title={tooltipText}>
							<span style={{ display: "flex", gap: "5px" }}>
								{disabled && excludedMark()}
								{task.task_name}
							</span>
						</Tooltip>
						{statusIcon(task.status)}
					</div>
				);
			});
		};

		return filteredPhaseList?.map((phase: any) => {
			const currentTaskList = filteredTaskList || taskList;
			const disabled = phase.disabled || false;
			const comment = phase.disabled_reason || "";
			const tooltipText = !disabled ? (
				"Click for phase overview"
			) : (
				<span>
					This phase is excluded from the well programme. You may still click
					for phase overview. <br /> <br />
					<b>Comment:</b> <br />
					{comment}
				</span>
			);

			return (
				<div
					style={{
						minWidth: "320px",
						maxWidth: "320px",
						height: "100%",
						display: "flex",
						flexDirection: "column",
						gap: "30px",
					}}
				>
					<div className="phase-progress-bar-bg">
						<span style={{ zIndex: "1" }}>
							{disabled ? "-" : <>{Math.round(phase.progress) || 0}%</>}
						</span>
						{!disabled && (
							<div
								className="phase-progress-bar"
								style={{
									width: `${Math.round(phase.progress) || 0}%`,
								}}
							></div>
						)}
					</div>
					<div className="project-status-container phase-container">
						<div
							className={
								!disabled
									? "project-status-container-header phase-item"
									: "project-status-container-header phase-item phase-item-disabled"
							}
							onClick={() => {
								navigate(`${phase._id}`, {
									replace: false,
									state: { phase_id: phase._id },
								});
							}}
							onContextMenu={(e: any) => {
								const location = `${window.location.href}/${phase._id}`;
								handleContextMenu(e, { link: location });
							}}
						>
							{GetAntIcon("square-check")}
							<Tooltip title={tooltipText}>
								<span style={{ display: "flex", gap: "5px" }}>
									{disabled && excludedMark()}
									{phase.phase_name}
								</span>
							</Tooltip>
						</div>
						<div
							className={
								!disabled
									? "project-status-container-content task-container"
									: "project-status-container-content task-container task-container-disabled"
							}
						>
							{renderTasks(phase)}
						</div>
					</div>
				</div>
			);
		});
	};

	if (hasViewOperationPermission.isLoading) {
		return <></>;
	}

	if (isExaminerOnly || !hasViewOperationPermission.hasPermission) {
		return (
			<NoAccess text="Oops, looks like you don't have the authorisation to view operations." />
		);
	}

	return (
		<>
			{projectInfo && wellInfo && !isLoading && (
				<div className="well-overview generic-container">
					{/* Top Container */}
					<div className="main-container" id="main-container">
						<div
							className="container-header"
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<div
								className="panel-info"
								onClick={() => {
									setWellDetailsExpand(!wellDetailsExpand);
								}}
							>
								{/* Well Details */}
								<span>Well - operation details</span>
								{wellDetailsExpand ? GetAntIcon("up") : GetAntIcon("downarrow")}
							</div>
							{/* Panel Buttons Container */}
							<div className="panel-main-buttons-container">
								{(hasEditOperationPermission ||
									hasDeleteOperationPermission) && (
									<a
										onClick={() => {
											navigate("operation-settings", {
												replace: false,
												state: { ...projectInfo, ...wellInfo },
											});
										}}
										onContextMenu={(e: any) => {
											const location = `${window.location.href}/operation-settings`;
											handleContextMenu(e, { link: location });
										}}
									>
										{GetAntIcon("setting")} Operation settings
									</a>
								)}

								<AuditLogButton
									moduleKey="project-delivered"
									handleClick={() => {
										setAuditOpen(true);
									}}
								/>
							</div>
						</div>
						{/* Expanded Well Details */}
						{wellDetailsExpand ? (
							<div className="project-details-container">
								<div className="well-project-details-content">
									{renderMetaData()}
								</div>
								{/* Divider */}
								<Divider
									type="vertical"
									style={{ height: "100%", margin: "0px 20px" }}
								></Divider>
								<div style={{ flex: "1" }}>
									<Divider
										orientation="left"
										style={{ fontSize: "14px", marginTop: "0px" }}
									>
										Well details
									</Divider>
									<div>{renderDetailData()}</div>
								</div>
							</div>
						) : (
							<></>
						)}
					</div>
					{/* Bottom Container */}
					<div
						className="main-container"
						style={{
							minHeight: "85%",
							flex: 1,
							width: "100%",
							// maxHeight: "100%",
							overflow: "hidden",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<div className="project-header">
							<div
								style={{ display: "flex", alignItems: "center", gap: "10px" }}
							>
								<TabButton
									identifier="kanban"
									label="Kanban"
									icon={GetAntIcon("project")}
									activePage={activePage}
									setActivePage={setActivePage}
									lock={!checkModule("project-delivered")}
								/>
								<TabButton
									identifier="ganttChart"
									label="Gantt chart"
									icon={GetAntIcon("gantt")}
									activePage={activePage}
									setActivePage={setActivePage}
									lock={!checkModule("project-delivered")}
								/>
								<TabButton
									identifier="documents"
									label="Documents"
									icon={GetAntIcon("file2")}
									activePage={activePage}
									setActivePage={setActivePage}
									tooltip={{
										title: sharepointDisabled ? "Documents are disabled" : "",
										placement: "top",
									}}
									disabled={sharepointDisabled}
									lock={!checkModule("project-delivered")}
								/>
							</div>

							<div
								style={{
									display: "flex",
									flex: "1",
									justifyContent: "flex-end",
									alignItems: "center",
									gap: "15px",
								}}
							>
								{/* temporary closed (implementing new gantt chart) */}
								{/* <a
									style={{
										display: activePage === "ganttChart" ? "" : "none",
									}}
									onClick={() => {
										Emitter.emit("RESET-GANTT", null);
									}}
								>
									Reset
								</a> */}
								{checkModule("project-delivered") && (
									<>
										{activePage === "kanban" && (
											<InputSearch
												placeholder="Search by task name"
												setSearchValue={setSearchValue}
											/>
										)}

										<div style={{ display: "flex", flexDirection: "row" }}>
											<div
												className={
													filterMode || hasSelectedFilters()
														? "project-active-button"
														: ""
												}
												style={{
													padding: "4px 10px",
													cursor: "pointer",
													display: activePage === "documents" ? "none" : "",
												}}
												onClick={() => {
													setFilterMode(!filterMode);
												}}
											>
												{GetAntIcon("filter")}
											</div>
										</div>
									</>
								)}
							</div>
						</div>
						<div
							className="generic-content project-overview-container"
							style={{
								flex: 1,
								display: "flex",
								flexWrap: "inherit",
								flexDirection: "column",
								alignItems: "flex-start",
								padding: 0,
								gap: 0,
								overflowX: "auto",
								maxHeight: "100%",
							}}
						>
							<div
								className={`filter-area ${
									filterMode ? "show-filter" : "hide-filter"
								}`}
							>
								{filterMode && (
									<>
										<div
											style={{
												display: "flex",
												flexDirection: "row",
												gap: "10px",
												padding: "8px",
												marginRight: "20px",
												alignItems: "center",
												height: "40px",
											}}
										>
											Hide excluded items
											<Switch
												// checkedChildren="Hide excluded items"
												// unCheckedChildren="Show excluded items"
												defaultValue={hideExcluded}
												// style={{ padding: "8px" }}
												onChange={(checked: any) => {
													setHideExcluded(checked);
												}}
											/>
										</div>
										{activePage === "kanban" && filterMode && (
											<>
												<CheckboxDropdown
													options={projectRoleAssignmentList}
													onChange={(selectedValues) =>
														handleMenuClick("responsible_roles", selectedValues)
													}
													onClear={""}
													defaultValue={selectedFilters.responsible_roles}
													Key="responsible_roles"
													label="Responsible roles"
												/>
												<CheckboxDropdown
													options={projectRoleAssignmentList}
													onChange={(selectedValues) =>
														handleMenuClick("accountable_roles", selectedValues)
													}
													onClear={""}
													defaultValue={selectedFilters.accountable_roles}
													Key="accountable_roles"
													label="Accountable roles"
												/>
												<CheckboxDropdown
													options={projectRoleAssignmentList}
													onChange={(selectedValues) =>
														handleMenuClick("consulted", selectedValues)
													}
													onClear={""}
													defaultValue={selectedFilters.consulted}
													Key="consulted"
													label="Consulted"
												/>
												<CheckboxDropdown
													options={projectRoleAssignmentList}
													onChange={(selectedValues) =>
														handleMenuClick("informed_parties", selectedValues)
													}
													onClear={""}
													defaultValue={selectedFilters.informed_parties}
													Key="informed_parties"
													label="Informed parties"
												/>
												<CheckboxDropdown
													options={[
														{ label: "New", value: "New" },
														{
															label: "To do",
															value: "To do",
														},
														{ label: "In Progress", value: "In progress" },
														{ label: "Late", value: "Late" },
														{
															label: "Resolved",
															value: "Resolved",
														},
													]}
													onChange={(selectedValues) =>
														handleMenuClick("status", selectedValues)
													}
													onClear={""}
													defaultValue={selectedFilters.status}
													Key="status"
													label="Status"
												/>
												<div className="test">
													<Button type="link" onClick={handleClearAllFilters}>
														Reset All
													</Button>
												</div>
											</>
										)}
									</>
								)}
							</div>
							{activePage === "kanban" && (
								<ProtectedModuleRoute moduleKey="project-delivered">
									<div
										className="kanban-columns-container"
										style={{
											display: "flex",
											flex: 1,
											alignItems: "flex-start",
											padding: "40px 30px",
											gap: "25px",
											alignSelf: "stretch",
											overflow: "auto",
										}}
									>
										{phaseList?.length > 0 ? (
											renderKanbanColumns()
										) : (
											<Empty
												className="no-data-empty"
												style={{ margin: "auto", fontSize: "20px" }}
												image={elementalSvg}
												description="Programme for well is not built yet."
											/>
										)}
									</div>
								</ProtectedModuleRoute>
							)}
							{activePage === "ganttChart" && (
								<ProtectedModuleRoute moduleKey="project-delivered">
									<div
										id="gantt-container"
										style={{
											display: "flex",
											flexDirection: "column",
											flex: 1,
											width: "100%",
											alignItems: "center",
											padding: "40px 30px",
											gap: "40px",
										}}
									>
										<div style={{ height: "100%", width: "100%" }}>
											<GanttChart data={ganttData} />
										</div>
										{/* <div
										className="table-container"
										style={{
											width: "100%",
											flexGrow: 1,
											padding: "20px 28px",
											background: "rgba(0,0,0,0.15",
										}}
									>
										<Table
											rowClassName={(record: any) => {
												if (record.disabled) {
													return "ganttchart-table-row-disabled";
												} else {
													return "";
												}
											}}
											footer={undefined}
											pagination={false}
											style={{ width: "100%" }}
											className="ganttchart-table"
											dataSource={filteredGanttTableData || filteredPhaseList}
											scroll={tableHeight && { y: tableHeight, x: 0 }}
											tableLayout="fixed"
											columns={[
												{
													title: (
														<SearchButton
															handleSearch={handleSearch}
															headerName="Phase"
															columnKey="phase_name"
															key={"phase"}
														/>
													),
													dataIndex: "phase_name",
													render: (value: any, record: any) => (
														<span>
															{record.disabled && <span>{"(Excl.) "}</span>}
															{value}
														</span>
													),
													key: "name",
													width: 200,
												},
												{
													title: "Planned start date",
													dataIndex: "planned_start_date",
													key: "planned_start_date",
													render: (value: any) => (
														<span>
															{value && dayjs(value)?.format("D MMM YYYY")}
														</span>
													),
													sorter: (a: any, b: any) => {
														const stringA = a.planned_start_date || "";
														const stringB = b.planned_start_date || "";
														return stringA.localeCompare(stringB);
													},

													// width: 200,
												},
												{
													title: "Est. duration (days)",
													dataIndex: "estimated_duration_days",
													key: "estimated_duration_days",
													sorter: (a: any, b: any) => {
														const valueA = a.estimated_duration_days
															? a.estimated_duration_days
															: -Infinity;
														const valueB = b.estimated_duration_days
															? b.estimated_duration_days
															: -Infinity;
														return valueA - valueB;
													},

													// width: 200,
												},
											]}
										></Table>
									</div> */}
									</div>
								</ProtectedModuleRoute>
							)}

							<SharepointComponent
								setDisabled={setSharepointDisabled}
								type="operation"
								id={operationInfo?._id}
								name={operationInfo?.operation_name}
								visible={activePage === "documents"}
								rest={operationInfo}
							/>
						</div>
					</div>
					<AuditLogModal
						tenantKey={context?.currentTenantKey}
						identifierKeys={{
							project_code: projectInfo?.project_code,
							operation_code: wellInfo?.operation_code,
						}}
						type={"operation"}
						label={"Well operation "}
						open={auditOpen}
						handleClose={() => {
							setAuditOpen(false);
						}}
					/>
				</div>
			)}
			{!projectInfo && !isLoading && (
				<>
					<NoAccess text={"This project does not exist."} />
				</>
			)}
			{!wellInfo && projectInfo && !isLoading && (
				<>
					<NoAccess text={"This well does not exist."} />
				</>
			)}
		</>
	);
}

export default WellOverview;
